import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { reportsAPI, employeeAPI, leaveTypeAPI, officeExpenseAPI } from '../../lib/api';
import {
  CalendarCheck,
  CalendarOff,
  Building2,
  CreditCard,
  Receipt,
  Banknote,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  BarChart3,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getToken = () => localStorage.getItem('token');

const PRESETS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const COMPANIES = [
  { value: 'all', label: 'All Companies' },
  { value: 'empact_studio', label: 'Empact Studio' },
  { value: 'event_resource', label: 'Event Resource' },
];

const CLAIM_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'partially_approved', label: 'Partially Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
];

const STATUS_STYLES = {
  submitted: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  partially_approved: 'bg-orange-100 text-orange-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-emerald-100 text-emerald-800',
};

// Shared sortable table
const SortableTable = ({ columns, rows, testId }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-500" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid={testId}>
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {columns.map(col => (
              <th
                key={col.key}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                onClick={() => toggleSort(col.key)}
                data-testid={`sort-${col.key}`}
              >
                <span className="flex items-center">{col.label}<SortIcon col={col.key} /></span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
              {columns.map(col => (
                <td key={col.key} className={`px-4 py-2.5 text-sm ${col.align === 'right' ? 'text-right font-semibold' : 'text-slate-700'}`}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={columns.length} className="text-center py-8 text-slate-400">No data found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Shared export bar
const ExportBar = ({ exportPath, params, formats = ['excel', 'pdf'] }) => {
  const doExport = (fmt) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v && v !== 'all') qs.set(k, v); });
    const url = `${API_BASE}${exportPath}/${fmt}?${qs.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', '');
    // add auth header via fetch
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="flex items-center gap-2" data-testid="export-bar">
      {formats.includes('excel') && (
        <Button variant="outline" size="sm" onClick={() => doExport('excel')} data-testid="export-excel-btn">
          <FileSpreadsheet className="w-4 h-4 mr-1.5 text-green-600" /> Excel
        </Button>
      )}
      {formats.includes('pdf') && (
        <Button variant="outline" size="sm" onClick={() => doExport('pdf')} data-testid="export-pdf-btn">
          <FileText className="w-4 h-4 mr-1.5 text-red-600" /> PDF
        </Button>
      )}
    </div>
  );
};

// ===================== TAB 1: Attendance Report =====================
const AttendanceReport = () => {
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ company: 'all', employee_id: 'all', department: 'all', preset: 'this_month', start_date: '', end_date: '' });

  React.useEffect(() => { employeeAPI.getAll().then(r => setEmployees(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const p = { ...filters };
      if (p.preset !== 'custom') { delete p.start_date; delete p.end_date; }
      const r = await reportsAPI.attendance(p);
      setData(r.data);
    } catch (e) { alert('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const columns = [
    { key: 'employee_name', label: 'Employee Name' },
    { key: 'working_days', label: 'Working Days', align: 'right' },
    { key: 'present', label: 'Present', align: 'right' },
    { key: 'absent', label: 'Absent', align: 'right' },
    { key: 'half_days', label: 'Half Days', align: 'right' },
    { key: 'late_arrivals', label: 'Late Arrivals', align: 'right' },
    { key: 'wfh_days', label: 'WFH Days', align: 'right' },
    { key: 'on_leave', label: 'On Leave', align: 'right' },
  ];

  return (
    <div className="space-y-5" data-testid="attendance-report">
      <FilterBar>
        <FilterSelect label="Company" value={filters.company} onChange={v => setFilters(f => ({ ...f, company: v }))} options={COMPANIES} testId="att-company" />
        <FilterSelect label="Employee" value={filters.employee_id} onChange={v => setFilters(f => ({ ...f, employee_id: v }))} options={[{ value: 'all', label: 'All Employees' }, ...employees.map(e => ({ value: e.id, label: e.full_name }))]} testId="att-employee" />
        <FilterSelect label="Department" value={filters.department} onChange={v => setFilters(f => ({ ...f, department: v }))} options={[{ value: 'all', label: 'All Departments' }, ...departments.map(d => ({ value: d, label: d }))]} testId="att-department" />
        <FilterSelect label="Period" value={filters.preset} onChange={v => setFilters(f => ({ ...f, preset: v }))} options={PRESETS} testId="att-preset" />
        {filters.preset === 'custom' && <>
          <div className="space-y-1"><Label className="text-xs">Start</Label><DatePicker value={filters.start_date} onChange={v => setFilters(f => ({ ...f, start_date: v }))} placeholder="Start" data-testid="att-start" /></div>
          <div className="space-y-1"><Label className="text-xs">End</Label><DatePicker value={filters.end_date} onChange={v => setFilters(f => ({ ...f, end_date: v }))} placeholder="End" data-testid="att-end" /></div>
        </>}
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-9" data-testid="generate-att-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
          </Button>
        </div>
      </FilterBar>
      {data && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">Period: {data.period.start} to {data.period.end} &middot; {data.rows.length} employees</span>
            <ExportBar exportPath="/reports/attendance/export" params={filters} />
          </div>
          <SortableTable columns={columns} rows={data.rows} testId="att-report-table" />
        </div>
      )}
    </div>
  );
};

// ===================== TAB 2: Leave Report =====================
const LeaveReport = () => {
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ company: 'all', employee_id: 'all', leave_type_id: 'all', preset: 'this_month', start_date: '', end_date: '' });

  React.useEffect(() => {
    employeeAPI.getAll().then(r => setEmployees(r.data)).catch(() => {});
    leaveTypeAPI.getTypes().then(r => setLeaveTypes(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const p = { ...filters };
      if (p.preset !== 'custom') { delete p.start_date; delete p.end_date; }
      const r = await reportsAPI.leave(p);
      setData(r.data);
    } catch (e) { alert('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const columns = [
    { key: 'employee_name', label: 'Employee Name' },
    { key: 'leave_type', label: 'Leave Type' },
    { key: 'days_entitled', label: 'Days Entitled', align: 'right' },
    { key: 'days_taken', label: 'Days Taken', align: 'right' },
    { key: 'days_remaining', label: 'Days Remaining', align: 'right' },
    { key: 'unpaid_leave_days', label: 'Unpaid Leave', align: 'right' },
  ];

  return (
    <div className="space-y-5" data-testid="leave-report">
      <FilterBar>
        <FilterSelect label="Company" value={filters.company} onChange={v => setFilters(f => ({ ...f, company: v }))} options={COMPANIES} testId="leave-company" />
        <FilterSelect label="Employee" value={filters.employee_id} onChange={v => setFilters(f => ({ ...f, employee_id: v }))} options={[{ value: 'all', label: 'All Employees' }, ...employees.map(e => ({ value: e.id, label: e.full_name }))]} testId="leave-employee" />
        <FilterSelect label="Leave Type" value={filters.leave_type_id} onChange={v => setFilters(f => ({ ...f, leave_type_id: v }))} options={[{ value: 'all', label: 'All Types' }, ...leaveTypes.map(t => ({ value: t.id, label: t.name }))]} testId="leave-type" />
        <FilterSelect label="Period" value={filters.preset} onChange={v => setFilters(f => ({ ...f, preset: v }))} options={PRESETS} testId="leave-preset" />
        {filters.preset === 'custom' && <>
          <div className="space-y-1"><Label className="text-xs">Start</Label><DatePicker value={filters.start_date} onChange={v => setFilters(f => ({ ...f, start_date: v }))} placeholder="Start" /></div>
          <div className="space-y-1"><Label className="text-xs">End</Label><DatePicker value={filters.end_date} onChange={v => setFilters(f => ({ ...f, end_date: v }))} placeholder="End" /></div>
        </>}
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-9" data-testid="generate-leave-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
          </Button>
        </div>
      </FilterBar>
      {data && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">{data.rows.length} records</span>
            <ExportBar exportPath="/reports/leave/export" params={filters} />
          </div>
          <SortableTable columns={columns} rows={data.rows} testId="leave-report-table" />
          {data.summary && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-6 text-sm font-semibold text-[#1D35A0]" data-testid="leave-summary">
              <span>Total Entitled: {data.summary.days_entitled}</span>
              <span>Total Taken: {data.summary.days_taken}</span>
              <span>Total Remaining: {data.summary.days_remaining}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== TAB 3: Office Expense Report =====================
const OfficeExpenseReport = () => {
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ company: 'all', category: 'all', preset: 'this_month', start_date: '', end_date: '' });

  React.useEffect(() => { officeExpenseAPI.getCategories().then(r => setCategories(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const p = { ...filters };
      if (p.preset !== 'custom') { delete p.start_date; delete p.end_date; }
      const r = await reportsAPI.officeExpenses(p);
      setData(r.data);
    } catch (e) { alert('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const columns = [
    { key: 'date', label: 'Date' },
    { key: 'category', label: 'Category', render: (v) => <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{v}</span> },
    { key: 'vendor', label: 'Vendor' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount (Rs.)', align: 'right', render: (v) => `Rs. ${v?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },
  ];

  return (
    <div className="space-y-5" data-testid="expense-report">
      <FilterBar>
        <FilterSelect label="Company" value={filters.company} onChange={v => setFilters(f => ({ ...f, company: v }))} options={COMPANIES} testId="exp-company" />
        <FilterSelect label="Category" value={filters.category} onChange={v => setFilters(f => ({ ...f, category: v }))} options={[{ value: 'all', label: 'All Categories' }, ...categories.map(c => ({ value: c.name, label: c.name }))]} testId="exp-category" />
        <FilterSelect label="Period" value={filters.preset} onChange={v => setFilters(f => ({ ...f, preset: v }))} options={PRESETS} testId="exp-preset" />
        {filters.preset === 'custom' && <>
          <div className="space-y-1"><Label className="text-xs">Start</Label><DatePicker value={filters.start_date} onChange={v => setFilters(f => ({ ...f, start_date: v }))} placeholder="Start" /></div>
          <div className="space-y-1"><Label className="text-xs">End</Label><DatePicker value={filters.end_date} onChange={v => setFilters(f => ({ ...f, end_date: v }))} placeholder="End" /></div>
        </>}
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-9" data-testid="generate-exp-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
          </Button>
        </div>
      </FilterBar>
      {data && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">{data.rows.length} expenses</span>
            <ExportBar exportPath="/reports/office-expenses/export" params={filters} />
          </div>
          <SortableTable columns={columns} rows={data.rows} testId="exp-report-table" />
          {data.category_totals && Object.keys(data.category_totals).length > 0 && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200" data-testid="exp-totals">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {Object.entries(data.category_totals).map(([cat, amt]) => (
                  <span key={cat} className="text-slate-600">{cat}: <span className="font-semibold">Rs. {amt.toLocaleString('en-IN')}</span></span>
                ))}
                <span className="ml-auto text-base font-bold text-[#1D35A0]">Grand Total: Rs. {data.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== TAB 4: Reimbursement Report =====================
const ReimbursementReport = () => {
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ company: 'all', employee_id: 'all', category: 'all', claim_status: 'all', preset: 'this_month', start_date: '', end_date: '' });

  const REIMB_CATS = ['Travel', 'Meals', 'Accommodation', 'Courier/Postage', 'Client Entertainment', 'Supplies', 'Other'];

  React.useEffect(() => { employeeAPI.getAll().then(r => setEmployees(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const p = { ...filters };
      if (p.preset !== 'custom') { delete p.start_date; delete p.end_date; }
      const r = await reportsAPI.reimbursements(p);
      setData(r.data);
    } catch (e) { alert('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const columns = [
    { key: 'employee_name', label: 'Employee' },
    { key: 'date', label: 'Date' },
    { key: 'category', label: 'Category' },
    { key: 'amount_claimed', label: 'Claimed (Rs.)', align: 'right', render: (v) => `Rs. ${v?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },
    { key: 'amount_approved', label: 'Approved (Rs.)', align: 'right', render: (v) => v ? `Rs. ${v?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-' },
    { key: 'status', label: 'Status', render: (v) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[v] || 'bg-slate-100 text-slate-600'}`}>{v}</span> },
    { key: 'payment_reference', label: 'Payment Ref', render: (v) => v || '-' },
  ];

  return (
    <div className="space-y-5" data-testid="reimb-report">
      <FilterBar>
        <FilterSelect label="Company" value={filters.company} onChange={v => setFilters(f => ({ ...f, company: v }))} options={COMPANIES} testId="reimb-company" />
        <FilterSelect label="Employee" value={filters.employee_id} onChange={v => setFilters(f => ({ ...f, employee_id: v }))} options={[{ value: 'all', label: 'All Employees' }, ...employees.map(e => ({ value: e.id, label: e.full_name }))]} testId="reimb-employee" />
        <FilterSelect label="Category" value={filters.category} onChange={v => setFilters(f => ({ ...f, category: v }))} options={[{ value: 'all', label: 'All Categories' }, ...REIMB_CATS.map(c => ({ value: c, label: c }))]} testId="reimb-category" />
        <FilterSelect label="Status" value={filters.claim_status} onChange={v => setFilters(f => ({ ...f, claim_status: v }))} options={CLAIM_STATUSES} testId="reimb-status" />
        <FilterSelect label="Period" value={filters.preset} onChange={v => setFilters(f => ({ ...f, preset: v }))} options={PRESETS} testId="reimb-preset" />
        {filters.preset === 'custom' && <>
          <div className="space-y-1"><Label className="text-xs">Start</Label><DatePicker value={filters.start_date} onChange={v => setFilters(f => ({ ...f, start_date: v }))} placeholder="Start" /></div>
          <div className="space-y-1"><Label className="text-xs">End</Label><DatePicker value={filters.end_date} onChange={v => setFilters(f => ({ ...f, end_date: v }))} placeholder="End" /></div>
        </>}
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-9" data-testid="generate-reimb-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
          </Button>
        </div>
      </FilterBar>
      {data && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">{data.rows.length} claims</span>
            <ExportBar exportPath="/reports/reimbursements/export" params={filters} />
          </div>
          <SortableTable columns={columns} rows={data.rows} testId="reimb-report-table" />
          {data.totals && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-6 text-sm font-semibold text-[#1D35A0]" data-testid="reimb-totals">
              <span>Total Claimed: Rs. {data.totals.claimed.toLocaleString('en-IN')}</span>
              <span>Total Approved: Rs. {data.totals.approved.toLocaleString('en-IN')}</span>
              <span>Total Paid: Rs. {data.totals.paid.toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== TAB 5: Payroll Assist =====================
const PayrollAssist = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [filters, setFilters] = useState({ company: 'all', month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` });

  const generate = async () => {
    setLoading(true);
    try {
      const r = await reportsAPI.payroll(filters);
      setData(r.data);
    } catch (e) { alert('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const columns = [
    { key: 'employee_name', label: 'Name' },
    { key: 'designation', label: 'Designation' },
    { key: 'days_present', label: 'Present', align: 'right' },
    { key: 'days_absent', label: 'Absent', align: 'right' },
    { key: 'casual_leave', label: 'CL', align: 'right' },
    { key: 'sick_leave', label: 'SL', align: 'right' },
    { key: 'earned_leave', label: 'EL', align: 'right' },
    { key: 'unpaid_leave', label: 'Unpaid', align: 'right' },
    { key: 'compoff_taken', label: 'Comp-off', align: 'right' },
    { key: 'outstanding_reimbursements', label: 'Reimb. Rs.', align: 'right', render: (v) => v > 0 ? `Rs. ${v.toLocaleString('en-IN')}` : '-' },
  ];

  const doExport = () => {
    const qs = new URLSearchParams();
    if (filters.company !== 'all') qs.set('company', filters.company);
    qs.set('month', filters.month);
    const url = `${API_BASE}/reports/payroll/export/excel?${qs.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `payroll_${filters.month}.xlsx`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="space-y-5" data-testid="payroll-report">
      <FilterBar>
        <FilterSelect label="Company" value={filters.company} onChange={v => setFilters(f => ({ ...f, company: v }))} options={COMPANIES} testId="payroll-company" />
        <div className="space-y-1"><Label className="text-xs">Month</Label><Input type="month" value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))} className="h-9" data-testid="payroll-month" /></div>
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-9" data-testid="generate-payroll-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Report'}
          </Button>
        </div>
      </FilterBar>
      {data && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <span className="text-sm text-slate-500">{data.rows.length} employees &middot; {data.month}</span>
              <p className="text-xs text-amber-600 italic mt-0.5" data-testid="payroll-note">{data.note}</p>
            </div>
            <Button variant="outline" size="sm" onClick={doExport} data-testid="export-payroll-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1.5 text-green-600" /> Export Excel
            </Button>
          </div>
          <SortableTable columns={columns} rows={data.rows} testId="payroll-report-table" />
        </div>
      )}
    </div>
  );
};

// ===================== Shared Components =====================
const FilterBar = ({ children }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-end gap-3">{children}</div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1 min-w-0 sm:min-w-[150px]">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

// ===================== Main Reports Page =====================
const Reports = () => (
  <div className="space-y-5" data-testid="reports-page">
    <div>
      <h2 className="text-xl font-bold text-[#1D35A0]">Reports</h2>
      <p className="text-sm text-slate-500">Generate, view and export company reports</p>
    </div>
    <Tabs defaultValue="attendance" className="w-full">
      <div className="overflow-x-auto -mx-1 px-1 pb-1">
        <TabsList className="bg-slate-100 p-1 rounded-lg inline-flex w-auto min-w-full sm:min-w-0" data-testid="report-tabs">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-attendance">
            <CalendarCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />Attendance
          </TabsTrigger>
          <TabsTrigger value="leave" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-leave">
            <CalendarOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />Leave
          </TabsTrigger>
          <TabsTrigger value="office-expenses" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-office-expenses">
            <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />Office Exp
          </TabsTrigger>
          <TabsTrigger value="reimbursements" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-reimbursements">
            <Receipt className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />Reimburse
          </TabsTrigger>
          <TabsTrigger value="payroll" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-payroll">
            <Banknote className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />Payroll
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="attendance" className="mt-5"><AttendanceReport /></TabsContent>
      <TabsContent value="leave" className="mt-5"><LeaveReport /></TabsContent>
      <TabsContent value="office-expenses" className="mt-5"><OfficeExpenseReport /></TabsContent>
      <TabsContent value="reimbursements" className="mt-5"><ReimbursementReport /></TabsContent>
      <TabsContent value="payroll" className="mt-5"><PayrollAssist /></TabsContent>
    </Tabs>
  </div>
);

export default Reports;
